import { DateTime, Info, StringUnitLength } from 'luxon';
import { Optional, Inject, Injectable } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDateFormats, NativeDateAdapter } from '@angular/material/core';

// SEE https://github.com/angular/material2/issues/10427

/** Creates an array and fills it with values.
//copied from NativeDateAdapter
*/
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array(length);
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
}

// TODO(mmalerba): Remove when we no longer support safari 9.
/** Whether the browser supports the Intl API. */
const SUPPORTS_INTL_API = typeof Intl != 'undefined'; //copied from NativeDateAdapter

/** The default date names to use if Intl API is not available. */
const DEFAULT_DATE_NAMES = range(31, (i) => String(i + 1)); //copied from NativeDateAdapter

@Injectable()
export class LuxonDateAdapter extends DateAdapter<DateTime> {
  private _localeData: {
    firstDayOfWeek: number;
    longMonths: string[];
    shortMonths: string[];
    dates: string[];
    longDaysOfWeek: string[];
    shortDaysOfWeek: string[];
    narrowDaysOfWeek: string[];
  };

  constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
    super();
    this.setLocale(dateLocale);
  }

  setLocale(locale: string) {
    super.setLocale(locale);

    const dates: string[] = [];
    if (SUPPORTS_INTL_API) {
      const dtf = new Intl.DateTimeFormat(this.locale, { day: 'numeric' });
      dates.push(...range(31, (i) => this._stripDirectionalityCharacters(dtf.format(new Date(2017, 0, i + 1)))));
    } else {
      dates.push(...DEFAULT_DATE_NAMES);
    }

    const opt = { locale: this.locale };
    const unitLengths: StringUnitLength[] = ['long', 'short', 'narrow'];

    // LUXON returns the day's starting with monday, (monday in index 0), material expects starting from sunday...
    const [longDaysOfWeek, shortDaysOfWeek, narrowDaysOfWeek] = unitLengths.map((p) => {
      const days = Info.weekdays(p, opt).slice();
      days.unshift(days.pop());
      return days;
    });

    this._localeData = {
      firstDayOfWeek: 0, // assume sunday
      longMonths: Info.months('long', opt),
      shortMonths: Info.months('short', opt),
      dates,
      longDaysOfWeek,
      shortDaysOfWeek,
      narrowDaysOfWeek,
    };
  }

  getYear(date: DateTime): number {
    return date.year;
  }

  getMonth(date: DateTime): number {
    return date.month - 1; //The Datepicker uses this to index into the 0 indexed getMonthNames array so far as I can tell. Because Luxon uses 1-12 for months we need to subtract one.
  }

  getDate(date: DateTime): number {
    return date.day;
  }

  getDayOfWeek(date: DateTime): number {
    return date.weekday;
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return style == 'long' ? this._localeData.longMonths : this._localeData.shortMonths;
  }

  getDateNames(): string[] {
    return this._localeData.dates;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style == 'long') {
      return this._localeData.longDaysOfWeek;
    }
    if (style == 'short') {
      return this._localeData.shortDaysOfWeek;
    }
    return this._localeData.narrowDaysOfWeek;
  }

  getYearName(date: DateTime): string {
    if (SUPPORTS_INTL_API) {
      let dtf = new Intl.DateTimeFormat(this.locale, { year: 'numeric' });
      let valueOfDate = date.valueOf();
      return this._stripDirectionalityCharacters(dtf.format(valueOfDate));
    }
    return String(this.getYear(date));
  }

  getFirstDayOfWeek(): number {
    return this._localeData.firstDayOfWeek;
  }

  getNumDaysInMonth(date: DateTime): number {
    return date.daysInMonth;
  }

  clone(date: DateTime): DateTime {
    return date; //there is no point in cloning a Luxon DateTime since they are immutable.
  }

  createDate(year: number, month: number, date: number): DateTime {
    month += 1; //luxon utc uses 1-12 for dates, but datepicker passes in 0-11.
    let aDate = DateTime.utc(year, month, date);
    return aDate;
  }

  today(): DateTime {
    return DateTime.local();
  }

  format(date: DateTime, displayFormat: any): string {
    return date.toFormat(displayFormat);
  }

  addCalendarYears(date: DateTime, years: number): DateTime {
    return date.plus({ years: years });
  }

  addCalendarMonths(date: DateTime, months: number): DateTime {
    return date.plus({ months: months });
  }

  addCalendarDays(date: DateTime, days: number): DateTime {
    return date.plus({ days: days });
  }

  toIso8601(date: DateTime): string {
    return date.toISO();
  }

  isDateInstance(obj: any): boolean {
    return obj instanceof DateTime;
  }

  isValid(date: DateTime): boolean {
    return date.isValid;
  }

  invalid(): DateTime {
    return DateTime.invalid('Invalid set via luxon-date-adapter.');
  }

  parse(value: any, parseFormat: any): DateTime | null {
    if (value && typeof value === 'string') {
      // First try to parse an ISO date
      const isoDateAttempt = DateTime.fromISO(value);
      if (isoDateAttempt.isValid == true) {
        return isoDateAttempt;
      }

      // Otherwise try to parse according to specified format (useful for user entered values?).
      // This adapter allow multiple parse formats via pipe (|) separator
      const formats = parseFormat.split('|');
      for (const format of formats) {
        const formatDateAttempt = DateTime.fromFormat(value, format);
        if (formatDateAttempt.isValid) {
          return formatDateAttempt;
        }
      }
    }
    return value;
  }

  //copied from NativeDateAdapter
  /**
   * Strip out unicode LTR and RTL characters. Edge and IE insert these into formatted dates while
   * other browsers do not. We remove them to make output consistent and because they interfere with
   * date parsing.
   * @param str The string to strip direction characters from.
   * @returns The stripped string.
   */
  private _stripDirectionalityCharacters(str: string) {
    return str.replace(/[\u200e\u200f]/g, '');
  }

  deserialize(value: any): DateTime | null {
    let date;
    if (value instanceof Date) {
      date = DateTime.fromJSDate(value);
    }
    if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      date = DateTime.fromISO(value);
    }
    if (date && this.isValid(date)) {
      return date;
    }
    return super.deserialize(value);
  }
}
